import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'react-bootstrap';
import { IconDownload } from '@tabler/icons-react';
import { Button, Screen } from '../types';
import { apiService } from '../services/api';
import { useFilters } from '../contexts';
import { useToast } from '../hooks/useToast';
import { ConfirmModal } from './ConfirmModal';
import { ToastNotification } from './ToastNotification';
import { WorkflowModal } from './WorkflowModal';
import { readWorkflowMessage } from '../utils/workflowResult';
import { expandColumns, getValueByPath } from '../utils/columnExpander';
import { getColumnDisplayValue, getVisibleColumns } from '../utils/screenDisplayValue';
import { formatTimestampForDisplay, formatDateForDisplay } from '../utils/timestampUtils';
import { downloadFile, getFileName } from '../utils/fileUtils';
import { mergeUi, resolveAppearance } from '../utils/uiUtils';

/**
 * Read-only "display as table" rendering for a one-to-many form field.
 *
 * Renders ONE flat table: rows = the leaf child records, columns = the leaf
 * related screen's columns. Wrapper screens that only expose a single
 * one-to-many column are drilled through (e.g. checklist → attachments →
 * comments), so the user sees the comments directly instead of a table inside
 * a table. Cell values reuse the app's standard display logic (date/time,
 * many-to-one target column / linked screen, files).
 *
 * The related screen's screen-level buttons stay available: they run their
 * workflow with the parent form and record id, the same payload an editable
 * embedded ScreenDataTable sends, so a workflow can refresh the child rows.
 */

interface NestedTableFieldProps {
    screen: Screen;                 // the field's related screen
    filters: Record<string, any>;   // context filters (parent record)
    title?: string;
    parentFormId?: string;          // update form the table is embedded in
    parentRecordId?: number | string;
}

// Descend through screens whose only visible column is a one-to-many relation.
function resolveLeaf(screen: Screen): { leaf: Screen; path: string[] } {
    let leaf = screen;
    const path: string[] = [];
    let guard = 0;
    while (guard++ < 5) {
        const visible = (leaf.columns || []).filter(c => !c.screenHidden);
        const only = visible.length === 1 ? visible[0] : null;
        if (only && only.columnType === 'one-to-many' && only.relatedScreen?.columns) {
            path.push(only.code);
            leaf = only.relatedScreen as unknown as Screen;
            continue;
        }
        break;
    }
    return { leaf, path };
}

export const NestedTableField: React.FC<NestedTableFieldProps> = ({ screen, filters, title, parentFormId, parentRecordId }) => {
    const { getFilter } = useFilters();
    const queryClient = useQueryClient();
    const { toasts, showToast, hideToast } = useToast();
    const { leaf, path } = resolveLeaf(screen);
    const columns = expandColumns(getVisibleColumns(leaf.columns));
    const screenButtons = (screen.buttons || []).filter(b => b.type === 'screen');

    const [pendingButton, setPendingButton] = useState<Button | null>(null);
    const [workflowState, setWorkflowState] = useState<{ title: string; loading: boolean; success?: boolean; message?: string } | null>(null);

    const runButton = async (button: Button) => {
        setWorkflowState({ title: button.displayName, loading: true });
        try {
            const context = parentFormId ? { formId: parentFormId, id: parentRecordId } : { screenId: screen.id };
            const response = await apiService.executeWorkflow(button.url, { data: context });
            if (response.success) {
                const responseData = (response.data as any)?.data || response.data;
                const notice = readWorkflowMessage(responseData);
                setWorkflowState(null);
                showToast(notice?.text || 'Úspešne dokončené', notice?.type ?? 'success');
                // The workflow may have rewritten the child rows (and the parent's other tables).
                queryClient.invalidateQueries({ queryKey: ['nestedTable'] });
                queryClient.invalidateQueries({ queryKey: ['screenData'] });
            } else {
                setWorkflowState({ title: button.displayName, loading: false, success: false, message: response.message || 'Nastala chyba pri vykonávaní workflow' });
            }
        } catch (error) {
            setWorkflowState({ title: button.displayName, loading: false, success: false, message: 'Nastala chyba pri vykonávaní workflow' });
        }
    };

    const handleButtonClick = (button: Button) => {
        if (button.confirm) {
            setPendingButton(button);
        } else {
            void runButton(button);
        }
    };

    // Same filter resolution as ScreenDataTable.buildFilters: context filters
    // (screen.filters) + screenFilters resolved through column.relatedScreen.filters,
    // then the explicit `filters` prop (parent context) on top.
    const effectiveFilters: Record<string, any> = {};
    (screen.filters || []).forEach((f: any) => {
        const v = getFilter(f.code);
        if (v) effectiveFilters[f.code] = v;
    });
    const resolveScreenFilters = (s: any, depth = 0) => {
        if (depth > 10 || !s?.screenFilters?.length) return;
        s.screenFilters.forEach((sf: any) => {
            const col = s.columns?.find((c: any) => c.id === sf.screenColumnId);
            if (col?.relatedScreen) {
                // context filters of the related screen
                col.relatedScreen.filters?.forEach((f: any) => {
                    const v = getFilter(f.code);
                    if (v && !effectiveFilters[f.code]) effectiveFilters[f.code] = v;
                });
                // PARAMETER query-filter keys of the related screen
                (col.relatedScreen.queryParameters || []).forEach((key: string) => {
                    const v = getFilter(key);
                    if (v && !effectiveFilters[key]) effectiveFilters[key] = v;
                });
                resolveScreenFilters(col.relatedScreen, depth + 1);
            }
        });
    };
    resolveScreenFilters(screen);
    if (filters) {
        Object.entries(filters).forEach(([k, v]) => { effectiveFilters[k] = v; });
    }

    const { data, isLoading, isError } = useQuery({
        queryKey: ['nestedTable', screen.id, JSON.stringify(effectiveFilters)],
        queryFn: async () => {
            const res = await apiService.getScreenData(screen.id, effectiveFilters, '', null, null, null, 1000);
            if (!res.success) throw new Error('load failed');
            const payload: any = res.data;
            return Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
        },
        enabled: !!screen.id,
        staleTime: 15000,
    });

    // Flatten along the one-to-many path to the leaf records.
    let rows: any[] = Array.isArray(data) ? data : [];
    for (const code of path) {
        rows = rows.flatMap((r: any) => Array.isArray(r?.[code]) ? r[code] : []);
    }

    const renderCell = (row: any, col: any): React.ReactNode => {
        const value = getValueByPath(row, col.dataPath);
        if (value === null || value === undefined || value === '') return '';

        if (['file', 'image'].includes(col.columnType)) {
            const fd: any = value;
            if (fd && (fd.url || fd.filename)) {
                const name = getFileName(fd);
                return fd.url
                    ? (
                        <span
                            className="text-primary d-inline-flex align-items-center gap-1"
                            style={{ cursor: 'pointer' }}
                            onClick={() => downloadFile(fd)}
                            title="Stiahnuť súbor"
                        >
                            <IconDownload size={14} />{name}
                        </span>
                    )
                    : name;
            }
            return '';
        }
        if (col.columnType === 'one-to-many') return String(Array.isArray(value) ? value.length : (value ?? 0));
        if (col.columnType === 'boolean') {
            const b = value === true || value === 1 || value === '1' || value === 'true';
            return b ? 'ANO' : 'NIE';
        }
        if (col.columnType === 'timestamp') return formatTimestampForDisplay(value);
        if (col.columnType === 'date') return formatDateForDisplay(value);
        if (typeof value === 'object') return getColumnDisplayValue(value, col);
        return String(value);
    };

    // Same appearance pipeline as the main table: column appearance, then the row's rule for that column.
    const cellUiOf = (row: any, col: any) => mergeUi(
        resolveAppearance(col.appearance, 'column'),
        resolveAppearance(row?._appearance?.column?.[col.code], 'rule'),
    );

    const renderStyledCell = (row: any, col: any): React.ReactNode => {
        const content = renderCell(row, col);
        if (typeof content !== 'string' || content === '') return content;
        const ui = cellUiOf(row, col);
        const text = ui.truncate
            ? <span className="d-inline-block text-truncate align-bottom" style={{maxWidth: '16rem'}} title={content}>{content}</span>
            : content;
        return ui.badgeClass
            ? <span className={ui.badgeClass} style={ui.badgeStyle} title={ui.truncate ? content : undefined}>{text}</span>
            : text;
    };

    const colCount = columns.length || 1;

    return (
        <div className="mt-4">
            <ToastNotification toasts={toasts} onClose={hideToast} />
            {(title || screenButtons.length > 0) && (
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                    {title ? <h3 className="mb-0">{title}</h3> : <span />}
                    {screenButtons.length > 0 && (
                        <div className="d-flex gap-2">
                            {screenButtons
                                .slice()
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                .map(button => (
                                    <button
                                        key={button.id || button.url}
                                        type="button"
                                        data-e2e={`btn:${button.id || button.url}`}
                                        className={`btn btn-sm ${resolveAppearance(button.appearance, 'button').className || 'btn-outline-primary'}`}
                                        style={resolveAppearance(button.appearance, 'button').style}
                                        onClick={() => handleButtonClick(button)}
                                        title={button.displayName}
                                    >
                                        {button.displayName}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>
            )}
            <div className="card">
                <div className="table-responsive">
                    <table className="table card-table table-vcenter">
                        <thead>
                            <tr>
                                {columns.map(col => {
                                    const headerUi = resolveAppearance(col.appearance, 'column');
                                    return (
                                        <th
                                            key={`${col.id}-${col.dataPath.join('.')}`}
                                            className={headerUi.headerClassName || undefined}
                                            style={headerUi.headerStyle}
                                        >
                                            {col.displayName}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan={colCount} className="text-center py-3"><Spinner animation="border" size="sm" /></td></tr>
                            ) : isError ? (
                                <tr><td colSpan={colCount} className="text-center text-danger py-3">Nepodarilo sa načítať</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={colCount} className="text-center text-muted py-3">Žiadne záznamy</td></tr>
                            ) : rows.map((row, i) => {
                                const rowUi = resolveAppearance(row?._appearance?.row, 'rule');
                                return (
                                    <tr key={row?.id ?? i} className={rowUi.className || undefined} style={rowUi.style}>
                                        {columns.map(col => {
                                            const cellUi = cellUiOf(row, col);
                                            return (
                                                <td
                                                    key={`${col.id}-${col.dataPath.join('.')}`}
                                                    data-column-type={col.columnType}
                                                    className={cellUi.className || undefined}
                                                    style={cellUi.style}
                                                >
                                                    {renderStyledCell(row, col)}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {pendingButton?.confirm && (
                <ConfirmModal
                    show={true}
                    onHide={() => setPendingButton(null)}
                    onConfirm={() => {
                        const button = pendingButton;
                        setPendingButton(null);
                        void runButton(button);
                    }}
                    title={pendingButton.confirm.title}
                    message={pendingButton.confirm.body}
                    confirmText={pendingButton.confirm.confirmButtonText}
                    variant="warning"
                />
            )}

            <WorkflowModal
                show={workflowState !== null}
                onHide={() => setWorkflowState(null)}
                title={workflowState?.title || ''}
                isLoading={!!workflowState?.loading}
                success={workflowState?.success}
                message={workflowState?.message}
            />
        </div>
    );
};
